import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { AlertTriangle, CheckCircle2, ExternalLink, Play, RefreshCw, ShieldAlert, Unlock, XCircle } from 'lucide-react';
import { Button, Card } from '../components';
import {
  beoordeelVerlenging,
  ComplianceHold,
  hefHoldOp,
  herbeoordeelProfessional,
  startVettingRun,
  vettingOverzicht,
  vettingVerlengingen,
} from '../services/complianceVettingApi';

/**
 * Admin: Continuous Vetting Engine (migratie 166). Nachtelijke runs, actieve
 * NON_COMPLIANT_HOLDs en de wachtrij met geüploade verlengingen. Goedkeuren
 * vraagt een geldig-tot-datum (Geldigheids-Token) en herbeoordeelt direct.
 */

const fmt = (iso: string | null) =>
  iso
    ? new Date(iso.length === 10 ? `${iso}T00:00:00` : iso).toLocaleString('nl-NL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        ...(iso.length > 10 ? { hour: '2-digit', minute: '2-digit' } : {}),
      })
    : '—';
const docUrl = (url: string) => `/${String(url).replace(/^\/+/, '')}`;
const foutTekst = (e: unknown, standaard: string) =>
  (e as { response?: { data?: { message?: string } } })?.response?.data?.message || standaard;

const AdminCompliancePage: React.FC = () => {
  const { t } = useTranslation('pages');
  const queryClient = useQueryClient();
  const [geldigTot, setGeldigTot] = useState<Record<number, string>>({});
  const [ophef, setOphef] = useState<ComplianceHold | null>(null);
  const [reden, setReden] = useState('');

  const overzicht = useQuery({ queryKey: ['admin-vetting-overzicht'], queryFn: vettingOverzicht });
  const verlengingen = useQuery({ queryKey: ['admin-vetting-verlengingen'], queryFn: vettingVerlengingen });
  const ververs = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-vetting-overzicht'] });
    queryClient.invalidateQueries({ queryKey: ['admin-vetting-verlengingen'] });
  };

  const beoordeel = useMutation({
    mutationFn: (v: { id: number; status: 'approved' | 'rejected'; expires_at?: string | null }) =>
      beoordeelVerlenging(v.id, v.status, v.expires_at),
    onSuccess: (r) => {
      toast.success(r?.data?.actie === 'OPHEFFEN' ? t('adminVetting.reviewedLifted') : t('adminVetting.reviewed'));
      ververs();
    },
    onError: (e) => toast.error(foutTekst(e, t('adminVetting.error'))),
  });
  const herbeoordeel = useMutation({
    mutationFn: herbeoordeelProfessional,
    onSuccess: (r) => {
      toast.success(t('adminVetting.recheckDone', { actie: r?.data?.actie ?? '—' }));
      ververs();
    },
    onError: (e) => toast.error(foutTekst(e, t('adminVetting.error'))),
  });
  const hefOp = useMutation({
    mutationFn: (v: { id: string; reden: string }) => hefHoldOp(v.id, v.reden),
    onSuccess: () => {
      toast.success(t('adminVetting.lifted'));
      setOphef(null);
      setReden('');
      ververs();
    },
    onError: (e) => toast.error(foutTekst(e, t('adminVetting.error'))),
  });
  const run = useMutation({
    mutationFn: startVettingRun,
    onSuccess: () => toast.success(t('adminVetting.runStarted')),
    onError: (e) => toast.error(foutTekst(e, t('adminVetting.error'))),
  });

  const laatste = overzicht.data?.runs[0];
  const holds = overzicht.data?.holds ?? [];

  return (
    <div className="py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ShieldAlert className="w-6 h-6 text-[#0B1F3A]" /> {t('adminVetting.title')}
            </h1>
            <p className="text-sm text-gray-500 mt-1">{t('adminVetting.subtitle')}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={ververs}>
              <RefreshCw className="w-4 h-4 mr-1" /> {t('adminVetting.refresh')}
            </Button>
            <Button loading={run.isPending} onClick={() => run.mutate()}>
              <Play className="w-4 h-4 mr-1" /> {t('adminVetting.runNow')}
            </Button>
          </div>
        </div>

        {/* Laatste run */}
        <Card>
          <div className="p-4">
            <h2 className="font-semibold text-gray-900 mb-2">{t('adminVetting.lastRun')}</h2>
            {!laatste ? (
              <p className="text-sm text-gray-500">{t('adminVetting.noRuns')}</p>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-7 gap-3 text-sm">
                  <Stat label={t('adminVetting.started')} waarde={fmt(laatste.gestart_op)} />
                  <Stat label={t('adminVetting.checked')} waarde={laatste.gecontroleerd} />
                  <Stat label={t('adminVetting.kvkCalls')} waarde={laatste.kvk_calls} />
                  <Stat label={t('adminVetting.bigCalls')} waarde={laatste.big_calls} />
                  <Stat label={t('adminVetting.newHolds')} waarde={laatste.nieuwe_holds} />
                  <Stat label={t('adminVetting.escalated')} waarde={laatste.geescaleerd} />
                  <Stat label={t('adminVetting.liftedCount')} waarde={laatste.opgeheven} />
                </div>
                {!laatste.klaar_op && <p className="mt-2 text-xs text-amber-700">{t('adminVetting.running')}</p>}
                {laatste.fouten > 0 && <p className="mt-2 text-xs text-red-600">{t('adminVetting.errors', { n: laatste.fouten })}</p>}
                {laatste.melding && (
                  <p className="mt-2 flex items-start gap-1 rounded bg-amber-50 p-2 text-xs text-amber-800">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {laatste.melding}
                  </p>
                )}
              </>
            )}
          </div>
        </Card>

        {/* Actieve holds */}
        <Card>
          <div className="p-4">
            <h2 className="font-semibold text-gray-900 mb-3">
              {t('adminVetting.activeHolds')} <span className="text-gray-400 font-normal">({holds.length})</span>
            </h2>
            {overzicht.isLoading ? (
              <div className="p-6 text-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0B1F3A] mx-auto" /></div>
            ) : holds.length === 0 ? (
              <p className="text-sm text-gray-500">{t('adminVetting.noHolds')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-100">
                      <th className="py-2 pr-3">{t('adminVetting.colProfessional')}</th>
                      <th className="py-2 pr-3">{t('adminVetting.colSeverity')}</th>
                      <th className="py-2 pr-3">{t('adminVetting.colReasons')}</th>
                      <th className="py-2 pr-3">{t('adminVetting.colDeadline')}</th>
                      <th className="py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {holds.map((h) => (
                      <tr key={h.id} className="border-b border-gray-50 align-top">
                        <td className="py-2.5 pr-3">
                          <p className="font-medium text-gray-900">{h.naam || '—'}</p>
                          <p className="text-xs text-gray-500">{h.email}</p>
                        </td>
                        <td className="py-2.5 pr-3">
                          <span
                            className={`rounded px-1.5 py-0.5 text-xs font-semibold ${
                              h.ernst === 'ZWAAR' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'
                            }`}
                          >
                            {t(`adminVetting.severity.${h.ernst}`)}
                          </span>
                        </td>
                        <td className="py-2.5 pr-3 text-gray-700">
                          <ul className="space-y-0.5">
                            {h.redenen.map((r, i) => (
                              <li key={i} className="text-xs">{r.detail}</li>
                            ))}
                          </ul>
                          {(h.open_uploads ?? 0) > 0 && (
                            <p className="mt-1 text-xs font-medium text-blue-700">{t('adminVetting.openUploads', { n: h.open_uploads })}</p>
                          )}
                        </td>
                        <td className="py-2.5 pr-3 text-xs text-gray-600 whitespace-nowrap">
                          {fmt(h.herstel_deadline)}
                          <p className="text-gray-400">{t('adminVetting.since', { date: fmt(h.gestart_op) })}</p>
                        </td>
                        <td className="py-2.5 text-right whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => herbeoordeel.mutate(h.user_id)}
                            disabled={herbeoordeel.isPending}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-[#0B1F3A] hover:underline mr-3"
                          >
                            <RefreshCw className="w-3.5 h-3.5" /> {t('adminVetting.recheck')}
                          </button>
                          <button
                            type="button"
                            onClick={() => setOphef(h)}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:underline"
                          >
                            <Unlock className="w-3.5 h-3.5" /> {t('adminVetting.lift')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Card>

        {/* Verlengingen */}
        <Card>
          <div className="p-4">
            <h2 className="font-semibold text-gray-900 mb-1">{t('adminVetting.renewals')}</h2>
            <p className="text-xs text-gray-500 mb-3">{t('adminVetting.renewalsHint')}</p>
            {(verlengingen.data ?? []).length === 0 ? (
              <p className="text-sm text-gray-500">{t('adminVetting.noRenewals')}</p>
            ) : (
              <div className="space-y-2">
                {(verlengingen.data ?? []).map((v) => {
                  const datum = geldigTot[v.id] ?? (v.expires_at ? String(v.expires_at).slice(0, 10) : '');
                  const datumNodig = v.document_type !== 'kvk';
                  return (
                    <div key={v.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900">
                          {v.naam || v.email}
                          {v.heeft_hold && (
                            <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-xs font-semibold text-red-700">{t('adminVetting.onHold')}</span>
                          )}
                        </p>
                        <p className="text-xs text-gray-600">
                          {t(`adminVetting.docType.${v.document_type}`)}
                          {v.professional_type ? ` · ${v.professional_type}` : ''} · {t('adminVetting.uploaded', { date: fmt(v.uploaded_at) })}
                        </p>
                      </div>
                      <a
                        href={docUrl(v.document_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:underline"
                      >
                        <ExternalLink className="w-3.5 h-3.5" /> {t('adminVetting.viewDoc')}
                      </a>
                      {datumNodig && (
                        <label className="text-xs text-gray-600">
                          {t('adminVetting.validUntil')}{' '}
                          <input
                            type="date"
                            value={datum}
                            onChange={(e) => setGeldigTot((g) => ({ ...g, [v.id]: e.target.value }))}
                            className="ml-1 rounded border border-gray-300 px-2 py-1 text-xs"
                          />
                        </label>
                      )}
                      <Button
                        size="sm"
                        disabled={beoordeel.isPending || (datumNodig && !datum)}
                        onClick={() => beoordeel.mutate({ id: v.id, status: 'approved', expires_at: datum || null })}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" /> {t('adminVetting.approve')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={beoordeel.isPending}
                        onClick={() => beoordeel.mutate({ id: v.id, status: 'rejected' })}
                      >
                        <XCircle className="w-4 h-4 mr-1" /> {t('adminVetting.reject')}
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Card>

        {/* Recente opheffingen */}
        {(overzicht.data?.opgeheven ?? []).length > 0 && (
          <Card>
            <div className="p-4">
              <h2 className="font-semibold text-gray-900 mb-2">{t('adminVetting.recentLifted')}</h2>
              <ul className="divide-y divide-gray-100 text-sm">
                {overzicht.data!.opgeheven.map((h) => (
                  <li key={h.id} className="flex flex-wrap justify-between gap-2 py-2">
                    <span className="text-gray-900">{h.naam || h.email}</span>
                    <span className="text-xs text-gray-500">
                      {fmt(h.opgeheven_op)} · {h.opheffing_reden}
                      {h.vrijgesteld_tot ? ` · ${t('adminVetting.exemptUntil', { date: fmt(h.vrijgesteld_tot) })}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </Card>
        )}

        {ophef && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true">
            <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
              <h2 className="text-lg font-bold text-gray-900">{t('adminVetting.liftTitle')}</h2>
              <p className="mt-1 text-sm text-gray-600">{t('adminVetting.liftText', { name: ophef.naam || ophef.email })}</p>
              <label className="mt-4 block text-sm font-medium text-gray-700">
                {t('adminVetting.reason')}
                <textarea
                  value={reden}
                  onChange={(e) => setReden(e.target.value)}
                  rows={3}
                  maxLength={900}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </label>
              <div className="mt-5 flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setOphef(null); setReden(''); }}>{t('adminVetting.cancel')}</Button>
                <Button
                  disabled={reden.trim().length < 5}
                  loading={hefOp.isPending}
                  onClick={() => hefOp.mutate({ id: ophef.id, reden: reden.trim() })}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {t('adminVetting.liftConfirm')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Stat: React.FC<{ label: string; waarde: React.ReactNode }> = ({ label, waarde }) => (
  <div>
    <p className="text-xs text-gray-500">{label}</p>
    <p className="font-semibold text-gray-900">{waarde}</p>
  </div>
);

export default AdminCompliancePage;
